import React, { useState, useEffect } from 'react';
import apiClient from '@/utils/apiClient';
import { useAuth } from '@/App';
import useParentData from '@/hooks/useParentData';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Calendar as CalendarIcon, Info, Award, Search, Sparkles, Umbrella } from 'lucide-react';
import { toast } from 'sonner';

// Formatting & Helper functions
const getHolidayTypeColor = (type) => {
  switch (type) {
    case 'National':
      return 'bg-red-50 text-red-700 border-red-200';
    case 'School':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'Optional':
      return 'bg-green-50 text-green-700 border-green-200';
    default:
      return 'bg-gray-50 text-gray-700 border-gray-200';
  }
};

const getHolidayTypeIcon = (type) => {
  switch (type) {
    case 'National':
      return <Award className="w-4 h-4 text-red-600" />;
    case 'School':
      return <Sparkles className="w-4 h-4 text-blue-600" />;
    default:
      return <Umbrella className="w-4 h-4 text-green-600" />;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  if (typeof dateString === 'string' && dateString.includes('-')) {
    const parts = dateString.split('-');
    if (parts[0].length === 4) {
      const [y, m, d] = parts;
      const dateObj = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
      return dateObj.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    } else if (parts[2]?.length === 4) {
      const [d, m, y] = parts;
      const dateObj = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
      return dateObj.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    }
  }
  const d = new Date(dateString);
  return isNaN(d.getTime()) ? dateString : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const isUpcoming = (dateString) => {
  if (!dateString) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let holidayDate;
  if (typeof dateString === 'string' && dateString.includes('-')) {
    const parts = dateString.split('-');
    if (parts[0].length === 4) {
      const [y, m, d] = parts;
      holidayDate = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
    } else if (parts[2]?.length === 4) {
      const [d, m, y] = parts;
      holidayDate = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
    }
  }
  if (!holidayDate) holidayDate = new Date(dateString);
  return holidayDate >= today;
};

// Main Presentational Layout
const HolidayListContainer = ({ holidays, loading, childrenSelector }) => {
  const [filterType, setFilterType] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredHolidays = holidays.filter((holiday) => {
    const matchesType = filterType === 'All' || holiday.holiday_type === filterType;
    const matchesSearch = holiday.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesType && matchesSearch;
  });

  const stats = React.useMemo(() => {
    const total = holidays.length;
    const upcoming = holidays.filter(h => isUpcoming(h.start_date)).length;
    const longest = holidays.reduce((max, h) => (h.total_days > max ? h.total_days : max), 0);
    return { total, upcoming, longest };
  }, [holidays]);

  if (loading) {
    return (
      <div className="p-8 text-center text-gray-500 flex flex-col items-center justify-center space-y-3">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2C5F7F]"></div>
        <p className="text-sm font-medium">Retrieving academic calendar...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-2">
      {childrenSelector}

      {/* Statistics Header Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border p-5 rounded-2xl shadow-sm flex items-center gap-4">
          <div className="p-3.5 bg-blue-50 rounded-xl">
            <CalendarIcon className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Total Holidays</p>
            <p className="text-2xl font-extrabold text-gray-800 mt-0.5">{stats.total}</p>
          </div>
        </div>

        <div className="bg-white border p-5 rounded-2xl shadow-sm flex items-center gap-4">
          <div className="p-3.5 bg-green-50 rounded-xl">
            <Sparkles className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Upcoming Holidays</p>
            <p className="text-2xl font-extrabold text-green-700 mt-0.5">{stats.upcoming}</p>
          </div>
        </div>

        <div className="bg-white border p-5 rounded-2xl shadow-sm flex items-center gap-4">
          <div className="p-3.5 bg-red-50 rounded-xl">
            <Umbrella className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Longest Vacation</p>
            <p className="text-2xl font-extrabold text-red-600 mt-0.5">{stats.longest} Days</p>
          </div>
        </div>
      </div>

      {/* Control bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-4 rounded-xl border shadow-sm">
        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          {['All', 'National', 'School', 'Optional'].map((type) => (
            <button
              key={type}
              onClick={() => setFilterType(type)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                filterType === type
                  ? 'bg-[#2C5F7F] text-white border-transparent'
                  : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {type} Holidays
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search holiday..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-gray-50/50 border-gray-200 focus-visible:ring-1 focus-visible:ring-[#2C5F7F] rounded-lg text-xs py-1.5"
          />
        </div>
      </div>

      {/* Holidays List Card */}
      <Card className="border shadow-sm rounded-2xl overflow-hidden bg-white">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Holiday Name</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Start Date</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">End Date</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Duration</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredHolidays.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-gray-400">
                      <Umbrella className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                      <p className="text-sm font-medium">No holidays found matching selected filters.</p>
                    </td>
                  </tr>
                ) : (
                  filteredHolidays.map((holiday) => {
                    const upcoming = isUpcoming(holiday.start_date);
                    return (
                      <tr key={holiday.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-6 py-4.5 font-bold text-gray-800 text-sm">
                          {holiday.name}
                        </td>
                        <td className="px-6 py-4.5 text-sm text-gray-600">
                          {formatDate(holiday.start_date)}
                        </td>
                        <td className="px-6 py-4.5 text-sm text-gray-600">
                          {formatDate(holiday.end_date)}
                        </td>
                        <td className="px-6 py-4.5">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${getHolidayTypeColor(holiday.holiday_type)}`}>
                            {getHolidayTypeIcon(holiday.holiday_type)}
                            {holiday.holiday_type}
                          </span>
                        </td>
                        <td className="px-6 py-4.5 text-sm font-medium text-gray-800">
                          {holiday.total_days} {holiday.total_days === 1 ? 'Day' : 'Days'}
                        </td>
                        <td className="px-6 py-4.5 text-right">
                          {upcoming ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-green-50 text-green-700 border border-green-200">
                              Upcoming
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
                              Passed
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Parent Portal Specific Component (calling hooks unconditionally)
const ParentHolidayView = () => {
  const { children, selectedChild, setSelectedChild } = useParentData();
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedChild?.id) {
      fetchParentHolidays(selectedChild.id);
    }
  }, [selectedChild]);

  const fetchParentHolidays = async (studentId) => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/v1/parent/child/${studentId}/holidays`);
      setHolidays(response.data.holidays || []);
    } catch (error) {
      toast.error('Failed to load holidays');
    } finally {
      setLoading(false);
    }
  };

  const childrenSelector = children && children.length > 1 ? (
    <div className="bg-white p-4 rounded-xl border shadow-sm flex items-center justify-between">
      <span className="text-sm font-semibold text-gray-600">Select Child:</span>
      <select
        className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#2C5F7F]"
        value={selectedChild?.id || ''}
        onChange={(e) => {
          const child = children.find(c => c.id === e.target.value);
          if (child) setSelectedChild(child);
        }}
      >
        {children.map(c => (
          <option key={c.id} value={c.id}>{c.name}</option>
        ))}
      </select>
    </div>
  ) : null;

  return (
    <HolidayListContainer
      holidays={holidays}
      loading={loading}
      childrenSelector={childrenSelector}
    />
  );
};

// Warden Portal Specific Component (calling hooks unconditionally)
const WardenHolidayView = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchWardenHolidays();
  }, []);

  const fetchWardenHolidays = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/v1/warden/holidays/');
      setHolidays(response.data.holidays || []);
    } catch (error) {
      toast.error('Failed to load holidays');
    } finally {
      setLoading(false);
    }
  };

  return (
    <HolidayListContainer
      holidays={holidays}
      loading={loading}
      childrenSelector={null}
    />
  );
};

// Main Routing Component
const HolidayView = () => {
  const { user } = useAuth();
  
  if (user?.role === 'parent') {
    return <ParentHolidayView />;
  }
  return <WardenHolidayView />;
};

export default HolidayView;
